import type { ComboboxOption, Tone } from '../../ui/v2';

export type TollFreeInventoryNumber = {
  phone_number: string;
  friendly_name?: string;
  capabilities?: {
    sms?: boolean;
    voice?: boolean;
  };
};

export type PurchaseResponse = {
  sid: string;
  phone_number: string;
  friendly_name: string;
  sms_url: string;
  sms_method: string;
  trunk_sid?: string;
};

export type PhoneNumberRegulatoryStatus = {
  phone_number: string;
  phone_number_sid: string;
  friendly_name?: string;
  voice_capable?: boolean;
  sms_capable?: boolean;
  mms_capable?: boolean;
  active_voice_config?: string | null;
  active_messaging_config?: string | null;
  voice_url?: string | null;
  voice_method?: string | null;
  sms_url?: string | null;
  sms_method?: string | null;
  trunk_sid?: string | null;
  trunk_friendly_name?: string | null;
  is_toll_free?: boolean;
  tollfree_verification_badge?: string | null;
  tollfree_verification_status?: string | null;
  tollfree_verification_sid?: string | null;
  tollfree_customer_profile_sid?: string | null;
  tollfree_customer_profile_name?: string | null;
  tollfree_rejection_reason?: unknown;
  tollfree_error_code?: string | null;
  tollfree_edit_allowed?: boolean;
  address_requirements?: string | null;
  bundle_sid?: string | null;
  bundle_status?: string | null;
};

export const twilioSmsWebhookUrl = (
  apiBaseUrl: string | undefined = process.env.REACT_APP_API_BASE_URL,
) => `${(apiBaseUrl ?? '').replace(/\/+$/, '')}/api/twilio/sms/`;

export const DEFAULT_SMS_URL = twilioSmsWebhookUrl();
export const DEFAULT_TRUNK_SID = 'TKf0e3985f2e4c375b2360797b8196dce8';
export const PHONE_NUMBERS_PAGE_SIZE = 20;
export const DEFAULT_SEARCH_LIMIT = 10;
export const SMS_METHODS = ['POST', 'GET'] as const;

export const readSelectedStoreId = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('currentStoreId') || '';
};

/** detail, then message, then error from a JSON body; otherwise the fallback. */
export const responseErrorMessage = async (
  response: Response,
  fallback: string,
) => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    return data.detail || data.message || data.error || fallback;
  }
  return fallback;
};

export interface PhoneNumbersPage {
  rows: PhoneNumberRegulatoryStatus[];
  count: number;
  totalPages: number;
}

export const EMPTY_PHONE_NUMBERS_PAGE: PhoneNumbersPage = {
  rows: [],
  count: 0,
  totalPages: 1,
};

export const parsePhoneNumbersPage = (data: any): PhoneNumbersPage => {
  const rows = Array.isArray(data?.phone_numbers)
    ? data.phone_numbers
    : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
        ? data
        : [];
  const count = data?.count || data?.total_count || rows.length;
  return {
    rows,
    count,
    totalPages:
      data?.total_pages ||
      Math.max(1, Math.ceil(count / PHONE_NUMBERS_PAGE_SIZE)),
  };
};

export const parseInventory = (data: any): TollFreeInventoryNumber[] =>
  Array.isArray(data?.phone_numbers) ? data.phone_numbers : [];

export const capabilitiesSummary = (number: TollFreeInventoryNumber) =>
  `${number.capabilities?.sms ? 'SMS' : 'No SMS'}, ${
    number.capabilities?.voice ? 'Voice' : 'No Voice'
  }`;

export const inventoryOptions = (
  inventory: TollFreeInventoryNumber[],
): ComboboxOption[] =>
  inventory.map((number) => ({
    value: number.phone_number,
    label: `${number.phone_number}${
      number.friendly_name ? ` - ${number.friendly_name}` : ''
    } (${capabilitiesSummary(number)})`,
  }));

export interface PurchaseForm {
  phoneNumber: string;
  friendlyName: string;
  smsUrl: string;
  smsMethod: string;
  trunkSid: string;
}

/** Only the phone number is always sent; overrides are trimmed and omitted when blank. */
export const buildPurchasePayload = (form: PurchaseForm) => {
  const payload: Record<string, string> = { phone_number: form.phoneNumber };
  if (form.friendlyName.trim())
    payload.friendly_name = form.friendlyName.trim();
  if (form.smsUrl.trim()) payload.sms_url = form.smsUrl.trim();
  if (form.smsMethod.trim()) payload.sms_method = form.smsMethod.trim();
  if (form.trunkSid.trim()) payload.trunk_sid = form.trunkSid.trim();
  return payload;
};

export const purchaseSuccessMessage = (data: PurchaseResponse) =>
  `Purchased ${data.phone_number}. SID: ${data.sid}`;

export const tfvSid = (number: PhoneNumberRegulatoryStatus | null) =>
  number?.tollfree_verification_sid?.trim() || '';

/** Twilio only deletes toll-free verification applications by their HH… SID. */
export const isDeletableTfvSid = (sid: string) => sid.startsWith('HH');

export const deleteTfvSuccessMessage = (data: any, verificationSid: string) => {
  const deletedSid = data?.deleted_sid || data?.sid || verificationSid;
  const updatedCount =
    data?.local_records_updated ?? data?.updated_count ?? data?.count;
  return `Deleted TFV application ${deletedSid}${
    updatedCount == null
      ? '.'
      : ` and marked ${updatedCount} local record${
          updatedCount === 1 ? '' : 's'
        } deleted.`
  }`;
};

export const tfvStatusTone = (status?: string | null): Tone => {
  if (!status || status === 'NOT_TOLL_FREE') return 'neutral';
  if (status === 'TWILIO_APPROVED' || status === 'twilio-approved') {
    return 'success';
  }
  if (status === 'TWILIO_REJECTED' || status === 'twilio-rejected') {
    return 'danger';
  }
  if (
    status === 'PENDING_REVIEW' ||
    status === 'IN_REVIEW' ||
    status === 'pending-review' ||
    status === 'in-review' ||
    status === 'provisionally-approved'
  ) {
    return 'warning';
  }
  return 'accent';
};

export const tfvStatusLabel = (number: PhoneNumberRegulatoryStatus) =>
  number.tollfree_verification_badge ||
  number.tollfree_verification_status ||
  'N/A';

export const displayValue = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value))
    return value.map(displayValue).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (record.reason || record.code) {
      return [record.code, record.reason]
        .map(displayValue)
        .filter(Boolean)
        .join(': ');
    }
    return JSON.stringify(value);
  }
  return String(value);
};

export const numberCapabilities = (number: PhoneNumberRegulatoryStatus) =>
  [
    ['Voice', number.voice_capable],
    ['SMS', number.sms_capable],
    ['MMS', number.mms_capable],
  ].map(([label, enabled]) => ({
    label: label as string,
    enabled: !!enabled,
  }));

/** `METHOD url` under a config, defaulting the method to POST; '' without a URL. */
export const webhookLine = (url?: string | null, method?: string | null) =>
  url ? `${method || 'POST'} ${url}` : '';
